import api from '@/libs/axiosInstance';
import type { ApiResponse } from '@fluenceur/types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

interface CreatorProfileImageResponse {
  id: string;
  profileImageUrl: string | null;
  isExpired: boolean;
  platform: string;
}

type BatchAvatarsResponse = Record<string, CreatorProfileImageResponse>;

/**
 * Fetch profile image data for creators using the batch endpoint
 * Handles automatic refresh of expired Instagram/Facebook CDN URLs
 * @param ids - Array of Instagram usernames
 * @returns Promise with creator profile image data mapped by username
 */
const fetchCreatorProfileImages = async (ids: string[]): Promise<BatchAvatarsResponse> => {
  const response = await api.post<ApiResponse<BatchAvatarsResponse>>(
    '/dashboard/creators/avatars',
    { ids }
  );
  return response.data.data as BatchAvatarsResponse;
};

/**
 * Fetch profile image data for a single creator
 * Uses the batch endpoint under the hood
 * @param creatorId - The Instagram username of the creator
 * @returns Promise with creator profile image data
 */
const fetchCreatorProfileImage = async (
  creatorId: string
): Promise<CreatorProfileImageResponse> => {
  const result = await fetchCreatorProfileImages([creatorId]);
  return result[creatorId];
};

interface UseCreatorProfileImageQueryOptions {
  creatorId: string | null | undefined;
  enabled?: boolean;
}

const CREATOR_PROFILE_IMAGE_QUERY_KEY_PREFIX = 'creatorProfileImage';

export const useCreatorProfileImageQuery = ({
  creatorId,
  enabled = true,
}: UseCreatorProfileImageQueryOptions): UseQueryResult<CreatorProfileImageResponse, Error> => {
  return useQuery<CreatorProfileImageResponse, Error>({
    queryKey: [CREATOR_PROFILE_IMAGE_QUERY_KEY_PREFIX, creatorId],
    queryFn: () => {
      if (!creatorId) {
        return Promise.reject(new Error('Creator ID is required.'));
      }
      return fetchCreatorProfileImage(creatorId);
    },
    enabled: !!creatorId && enabled,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - matches backend avatar cache
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days - garbage collection
    retry: 2,
  });
};

/**
 * Prefetch profile images for multiple creators at once
 * Populates individual query cache entries so CreatorAvatar components
 * can find the data immediately without making additional requests
 * @param ids - Array of Instagram usernames to prefetch avatars for
 */
export const usePrefetchCreatorProfileImages = (ids: string[]): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!ids || ids.length === 0) return;

    // Filter out IDs that are already in the cache
    const uncachedIds = ids.filter((id) => {
      const cached = queryClient.getQueryData([CREATOR_PROFILE_IMAGE_QUERY_KEY_PREFIX, id]);
      return !cached;
    });

    if (uncachedIds.length === 0) return;

    // Fetch all uncached avatars in a single batch request
    fetchCreatorProfileImages(uncachedIds)
      .then((result) => {
        // Populate individual cache entries for each creator
        Object.entries(result).forEach(([username, data]) => {
          queryClient.setQueryData([CREATOR_PROFILE_IMAGE_QUERY_KEY_PREFIX, username], data);
        });
      })
      .catch((error) => {
        console.warn('Failed to prefetch creator profile images:', error);
      });
  }, [ids, queryClient]);
};

// Export the fetch function for use in other hooks
export { fetchCreatorProfileImage, fetchCreatorProfileImages };
